import React from 'react';
import {FormattedMessage} from 'react-intl';
import {message, Modal, Form, Radio} from 'antd';
import useQueryApiClient from 'utils/useApiClient';
import FileDownload from 'js-file-download';
import {UsersExportModalInterface} from '../types';
import { useLocaleContext } from 'context/LocaleContext';
import {useT} from "../../../hooks/Locale/useT";
import moment from 'moment';

const UsersExportModal: React.FC<UsersExportModalInterface> = ({
    visible,
    onCancel,
    activeOrganizationId,
    activeOrganizationType,
    selectedRowKeys,
    exportType,
    filter,
    search,
}) => {
    const { locale } = useLocaleContext();
    const t = useT();

    const [exportForm] = Form.useForm();

    const timestamp = moment().unix();

    const {appendData: exportUsers, isLoading: exportLoading} = useQueryApiClient({
        request: {
            url: '/api/v1/auth/users/export',
            method: 'POST',
            mustRetry: true,
            multipart: true
        },
        onSuccess: (response) => {
            if (response) {
                FileDownload(response, `CloudStudy-Customer-Users-${timestamp}.xlsx`);
                message.success(t('users.export.successful_export_in_message'));
            }
        }
    });

    const {appendData: exportUsersWithRights} = useQueryApiClient({
        request: {
            url: '/api/v1/auth/users/export-with-rights',
            method: 'POST',
            mustRetry: true,
            multipart: true
        },
        onSuccess: (response) => {
            if (response) {
                FileDownload(response, `CloudStudy-Customer-Users-With-Rights-${timestamp}.xlsx`);
                message.success(t('users.export.successful_export_in_message'));
            }
        }
    });

    const exportCustomerUsers = async (values: any) => {
        if (!activeOrganizationId) {
            message.error(t('users.select_customer_in_filter_first'));
            return;
        }

        const bodyFormData = new FormData();

        bodyFormData.append('filterBy', activeOrganizationType?.toString() || '');
        bodyFormData.append('language', locale);

        if (activeOrganizationType === 'CUSTOMER') {
            bodyFormData.append('customerId', activeOrganizationId.toString());
        } else {
            bodyFormData.append('organizationId', activeOrganizationId.toString());
        }

        if (filter) {
            const scopeKeys = ['filter[filterBy]', 'filter[customerId]', 'filter[organizationId]'];

            new URLSearchParams(filter).forEach((value, key) => {
                if ((key.startsWith('filter[') || key.startsWith('customerFilter[')) && !scopeKeys.includes(key)) {
                    bodyFormData.append(key, value);
                }
            });
        }

        // The list narrows by the search string as well, so the export has to carry it too —
        // otherwise "export all" returns more rows than the user sees.
        if (search) {
            bodyFormData.append('filter[q]', search);
        }

        if (values.exportOption === 'selected') {
            if (!Array.isArray(selectedRowKeys) || !selectedRowKeys.length) {
                message.error(t('campaign.please_select_atleast_one_user'));
                return;
            }

            bodyFormData.append('selectedUsers', selectedRowKeys.join(','));
        }

        if (exportType === 'rights') {
            exportUsersWithRights(bodyFormData);
        } else {
            exportUsers(bodyFormData);
        }
    };

    return (
        <Modal
            className="user-list-modal"
            title={t('users.export.export')}
            open={visible}
            onOk={() => {
                exportForm.submit();
            }}
            confirmLoading={exportLoading}
            onCancel={onCancel}
            okText={t('general.export.send')}
            cancelText={t('general.cancel')}
        >
            <Form 
                form={exportForm} 
                onFinish={(v: any) => {
                    exportCustomerUsers(v);
                    onCancel();
                }}
            >
                <Form.Item
                    name="exportOption"
                    label={t('users.export.export_option')}
                >
                    <Radio.Group defaultValue='all'>
                        <Radio value="all">
                            <FormattedMessage id='users.export.export_all_users' />
                        </Radio>
                        <Radio value="selected">
                            <FormattedMessage id={t('users.export.export_selected_users', { amount: Array.isArray(selectedRowKeys) ? selectedRowKeys.length : 0 })}/>
                        </Radio>
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    )
};

export default UsersExportModal;
